import BrowserStorage from "../browser-store";
import i18n from '../i18n';
import { hasText, isObject } from 'stac-js/src/utils.js';

const KEY_METHOD = 'auth-last-method';
const KEY_ORIGINAL_URI = 'auth-original-uri';

export default class Auth {

  /**
   * Constructs the authentication method.
   * 
   * @param {Router} router The Vue router instance
   * @param {Object.<string, *>} options  Any potential options the authentication method needs
   * @param {Function} changeListener A change listener with two parameters: loggedIn (boolean) and credentials (string|null)
   */
  constructor(router, options = {}, changeListener = null) {
    this.options = isObject(options) ? options : {};
    this.changeListener = changeListener;
    this.router = router;
    this.browserStorage = new BrowserStorage(true);
  }

  /**
   * A tooltip title for the authentication button.
   * 
   * @returns {string}
   */
  getButtonTitle() {
    return null;
  }

  /**
   * A label for the button that is shown when unauthorized (logged out).
   * 
   * @returns {string}
   */
  getLoginLabel() {
    return i18n.global.t('authentication.button.login');
  }

  /**
   * A label for the button that is shown when authorized (logged in).
   * 
   * @returns {string}
   */
  getLogoutLabel() {
    return i18n.global.t('authentication.button.logout');
  }

  getComponent() {
    return null;
  }

  getComponentProps() {
    return {};
  }

  async init() {
  }

  async login() {
    this.storeSession();
  }

  async confirmLogin(credentials) {
    if (this.changeListener) {
      await this.changeListener(true, credentials);
    }
  }

  async logout(/*credentials*/) {
    this.storeSession();
  }

  // Tries to resume a user session after e.g. page reload.
  // Returns true if a session could be resumed, false otherwise.
  async resume() {
    return false;
  }

  async confirmLogout() {
    if (this.changeListener) {
      await this.changeListener(false);
    }
  }

  async close() {
    return;
  }

  storeSession() {
    this.persistMethod();
    this.setOriginalUri();
  }

  setOriginalUri() {
    this.browserStorage.set(KEY_ORIGINAL_URI, this.router.currentRoute.value.fullPath);
  }

  restoreOriginalUri() {
    let originalUri = this.browserStorage.get(KEY_ORIGINAL_URI);
    if (this.router && hasText(originalUri)) {
      if (originalUri.startsWith('/auth/logout')) {
        originalUri = '/';
      }
      this.router.replace(originalUri);
    }
    this.browserStorage.remove(KEY_ORIGINAL_URI);
  }

  static restoreLastMethod(clean = true) {
    const storage = new BrowserStorage(true);
    const method = storage.get(KEY_METHOD);
    if (clean) {
      storage.remove(KEY_METHOD);
    }
    return method;
  }

  persistMethod() {
    this.browserStorage.set(KEY_METHOD, this.options);
  }

  updateStore(/*value*/) {
    return {};
  }

  _updateStore(value, defaultName = null, defaultIn = null, defaultFormatter = null) {
    const formatter = this.options.formatter || defaultFormatter;
    const key = this.options.name || defaultName;
    const in_ = this.options.in || defaultIn;

    // Format the credentials
    if (value) {
      if (formatter === 'Bearer') {
        value = `Bearer ${value}`;
      }
      else if (typeof formatter === 'function') {
        value = formatter(value);
      }
    }
    if (!hasText(value)) {
      value = undefined;
    }

    // Set cookie, query or request parameters
    if (in_ === 'query') {
      return { query: { type: 'private', key, value } };
    }
    else if (in_ === 'cookie') {
      return { cookie: { key, value } };
    }
    else if (in_ === 'header') {
      return { header: { key, value } };
    }
    else {
      return {};
    }
  }

  static async create(router, config, changeListener) {
    let method = new Auth(router, config, changeListener);
    if (isObject(config)) {
      if (config.type === 'http' && config.scheme === 'basic') {
        const BasicAuth = (await import('./basic')).default;
        method = new BasicAuth(router, config, changeListener);
      }
      else if (config.type === 'apiKey') {
        const ApIKey = (await import('./apiKey')).default;
        method = new ApIKey(router, config, changeListener);
      }
      else if (config.type === 'openIdConnect') {
        const OIDC = (await import('./oidc')).default;
        method = new OIDC(router, config, changeListener);
      }
    }
    await method.init();
    return method;
  }

  static equals(method, config) {
    return JSON.stringify(method.options) === JSON.stringify(config);
  }

}
